const enterpriseServiceTradeTypes: Record<string, number> = {
  'biz-base-1year': 50,
  'biz-mid-1year': 51,
  'biz-high-1year': 52,
  'biz-global-base-1year': 74,
  'biz-global-professional-1year': 75,
  'biz-global-advanced-1year': 76,
  'biz-global-add-oil-01': 77,
}

export type EnterpriseFuelMode = 'none' | 'local' | 'global'

export interface GlobalFuelAllocation {
  aiQuota: number
  vipQuota: number
}

interface EnterpriseFuelEntitlement {
  serviceLevel?: number | null
  serviceEndTime?: string | null
  globalContractNo?: string | null
}

export interface EnterprisePlanEntitlement extends EnterpriseFuelEntitlement {
  globalPlanCode?: string | null
  regionMode?: string | null
  legacyMigrationNo?: string | null
}

/** 迁移区域选择限制后续购买家族，不改变原套餐等级、加油包或实际权益。 */
export function isLegacyCrossRegionEnterprise(enterprise?: EnterprisePlanEntitlement | null): boolean {
  return enterprise?.regionMode === 'CROSS_REGION' && Boolean(enterprise.legacyMigrationNo?.trim())
}

export interface EnterprisePlan {
  mode: 'local' | 'global'
  rank: number
}

export type EnterprisePlanBlockReason = 'unknown-plan' | 'unknown-entitlement' | 'global-to-local' | 'downgrade' | 'current-plan'
export type EnterprisePlanPurchaseDecision =
  | { allowed: true; action: 'purchase' | 'upgrade'; reason?: never }
  | { allowed: false; action?: never; reason: EnterprisePlanBlockReason }

export interface EnterpriseQuotaEntitlement extends EnterpriseFuelEntitlement {
  remainServiceWords?: number | null
  allServiceWordsDetail?: string | null
  remainPackageWords?: number | null
  allPackageWordsDetail?: string | null
  totalPackageWords?: number | null
}

export interface EnterpriseQuotaSnapshot {
  serviceTotalWords: number
  serviceRemainingWords: number
  serviceUsedWords: number
  packageTotalWords: number
  packageRemainingWords: number
  packageUsedWords: number
  totalWords: number
  remainingWords: number
  usedWords: number
}

const GLOBAL_SERVICE_LEVEL_MIN = 5

export function isGlobalEnterpriseServiceLevel(serviceLevel?: number | null): boolean {
  return Number(serviceLevel || 0) >= GLOBAL_SERVICE_LEVEL_MIN
}

/** 套餐家族与档位分开判断，避免把全球基础版的等级 6 当成高于独立高阶版。 */
export function getCurrentEnterprisePlan(
  enterprise?: EnterprisePlanEntitlement | null,
  now = Date.now(),
): EnterprisePlan | null {
  const mode = getEnterpriseFuelMode(enterprise, now)
  if (mode === 'none') return null
  const level = Number(enterprise?.serviceLevel)
  if (mode === 'local') return { mode, rank: level >= 1 && level <= 4 ? level : 0 }
  const legacyRanks: Record<string, number> = { BASE: 1, PROFESSIONAL: 2, ADVANCED: 3 }
  const rank = level === 5
    ? legacyRanks[String(enterprise?.globalPlanCode || '').toUpperCase()] || 0
    : level >= 6 && level <= 8 ? level - 5 : 0
  return { mode, rank }
}

export function getEnterprisePlanByChoiceId(choiceId?: string): EnterprisePlan | null {
  const tradeType = getEnterpriseTradeType(choiceId)
  if (tradeType !== null && tradeType >= 50 && tradeType <= 52) return { mode: 'local', rank: tradeType - 49 }
  if (tradeType !== null && tradeType >= 74 && tradeType <= 76) return { mode: 'global', rank: tradeType - 73 }
  return null
}

export function getEnterprisePlanPurchaseDecision(
  enterprise?: EnterprisePlanEntitlement | null,
  choiceId?: string,
  now = Date.now(),
): EnterprisePlanPurchaseDecision {
  const target = getEnterprisePlanByChoiceId(choiceId)
  if (!target) return { allowed: false, reason: 'unknown-plan' }
  // 区域选择在旧权益到期后仍有效，必须先于无有效套餐的首次购买分支校验。
  if (isLegacyCrossRegionEnterprise(enterprise) && target.mode === 'local') {
    return { allowed: false, reason: 'global-to-local' }
  }
  const current = getCurrentEnterprisePlan(enterprise, now)
  if (!current) return { allowed: true, action: 'purchase' }
  if (!current.rank) return { allowed: false, reason: 'unknown-entitlement' }
  if (current.mode === 'global' && target.mode === 'local') return { allowed: false, reason: 'global-to-local' }
  if (target.rank < current.rank) return { allowed: false, reason: 'downgrade' }
  if (current.mode === target.mode && current.rank === target.rank) return { allowed: false, reason: 'current-plan' }
  return { allowed: true, action: 'upgrade' }
}

export function getEnterprisePlanBlockMessage(reason: EnterprisePlanBlockReason): string {
  const messages: Record<EnterprisePlanBlockReason, string> = {
    'unknown-plan': '请选择可购买的套餐',
    'unknown-entitlement': '当前套餐档位暂无法确认，请联系商务',
    'global-to-local': '全球跨区套餐不可回退独立区套餐',
    downgrade: '不支持降档，请选择同档全球套餐或更高档套餐',
    'current-plan': '当前套餐暂不支持重复购买或同档续费',
  }
  return messages[reason]
}

export function getEnterprisePurchaseError(code?: string, fallback = '当前账号暂不满足套餐购买条件'): string {
  const messages: Record<string, string> = {
    '10022': '当前选择不符合升级规则：不能降档、回退独立区或同档续费',
    '10057': '全球套餐仅支持使用邮箱和密码登录的账号购买',
    '10058': '当前邮箱账号尚未设置可同步密码，请先设置密码',
    '10059': '只有当前企业的创建者可以购买并执行双区关联',
    '10062': '请前往全球套餐的购买站点办理升级',
    '10064': '套餐状态已变化或有未完成的升级，请刷新后重试',
    '10065': '当前企业已有全球合同，请先完成待处理的套餐开通',
  }
  return messages[code || ''] || fallback
}

export function getEnterpriseFuelMode(
  enterprise?: EnterpriseFuelEntitlement | null,
  now = Date.now(),
): EnterpriseFuelMode {
  if (!enterprise) return 'none'

  const serviceLevel = Number(enterprise.serviceLevel || 0)
  // 全球合同字段会在取消后作为历史关联继续保留，当前套餐类型只能由服务等级判断。
  if (serviceLevel <= 0) return 'none'

  const serviceEndTime = enterprise.serviceEndTime ? new Date(enterprise.serviceEndTime).getTime() : Number.NaN
  if (Number.isFinite(serviceEndTime) && serviceEndTime <= now) return 'none'
  return isGlobalEnterpriseServiceLevel(serviceLevel) ? 'global' : 'local'
}

function normalizeQuota(value: unknown): number {
  const quota = Number(value || 0)
  return Number.isFinite(quota) ? Math.max(0, quota) : 0
}

function latestQuota(detail?: string | null): number {
  const values = String(detail || '').split(',')
  for (let index = values.length - 1; index >= 0; index -= 1) {
    if (!values[index]?.trim()) continue
    const quota = Number(values[index]?.trim())
    if (Number.isFinite(quota)) return Math.max(0, quota)
  }
  return 0
}

function sumQuota(detail?: string | null): number {
  return String(detail || '').split(',').reduce((total, value) => {
    const quota = Number(value.trim())
    return total + (Number.isFinite(quota) ? Math.max(0, quota) : 0)
  }, 0)
}

/**
 * 统一当前企业套餐的额度口径。
 *
 * 套餐明细取当前周期，加油包明细累计；取消时后端清空作废的加油包明细。
 * 余额耗尽不等于额度作废，不能因余额为零或留有历史合同号而丢掉已用量。
 */
export function getEnterpriseQuotaSnapshot(
  enterprise?: EnterpriseQuotaEntitlement | null,
  now = Date.now(),
): EnterpriseQuotaSnapshot {
  const mode = getEnterpriseFuelMode(enterprise, now)
  if (mode === 'none') {
    return {
      serviceTotalWords: 0,
      serviceRemainingWords: 0,
      serviceUsedWords: 0,
      packageTotalWords: 0,
      packageRemainingWords: 0,
      packageUsedWords: 0,
      totalWords: 0,
      remainingWords: 0,
      usedWords: 0,
    }
  }

  const serviceRemaining = normalizeQuota(enterprise?.remainServiceWords)
  const packageRemaining = normalizeQuota(enterprise?.remainPackageWords)
  const serviceTotalWords = Math.max(serviceRemaining, latestQuota(enterprise?.allServiceWordsDetail))
  const explicitPackageTotal = normalizeQuota(enterprise?.totalPackageWords)
  const packageTotalWords = Math.max(packageRemaining, explicitPackageTotal || sumQuota(enterprise?.allPackageWordsDetail))
  const remainingWords = serviceRemaining + packageRemaining
  const totalWords = Math.max(remainingWords, serviceTotalWords + packageTotalWords)

  return {
    serviceTotalWords,
    serviceRemainingWords: serviceRemaining,
    serviceUsedWords: Math.max(0, serviceTotalWords - serviceRemaining),
    packageTotalWords,
    packageRemainingWords: packageRemaining,
    packageUsedWords: Math.max(0, packageTotalWords - packageRemaining),
    totalWords,
    remainingWords,
    usedWords: Math.max(0, totalWords - remainingWords),
  }
}

export function getGlobalFuelAutomaticAllocation(
  totalQuota: number,
  aiQuota?: number,
  vipQuota?: number,
): GlobalFuelAllocation {
  const total = Math.max(0, Math.floor(Number(totalQuota) || 0))
  const currentAi = Math.floor(Number(aiQuota))
  const currentVip = Math.floor(Number(vipQuota))
  if (Number.isFinite(currentAi) && Number.isFinite(currentVip)
    && currentAi >= 0 && currentVip >= 0 && currentAi + currentVip === total) {
    return { aiQuota: currentAi, vipQuota: currentVip }
  }

  const defaultVipQuota = Math.floor(total / 2)
  return {
    aiQuota: total - defaultVipQuota,
    vipQuota: defaultVipQuota,
  }
}

export function getEnterpriseTradeType(priceChoiceId?: string): number | null {
  if (!priceChoiceId) return null

  const serviceChoiceId = priceChoiceId.replace(/-[a-z]{3}$/i, '')
  const serviceTradeType = enterpriseServiceTradeTypes[serviceChoiceId]
  if (serviceTradeType !== undefined) return serviceTradeType

  const fuelMatch = priceChoiceId.match(/^biz-add-oil-0([1-6])-[a-z]{3}$/i)
  return fuelMatch ? 59 + Number(fuelMatch[1]) : null
}
